import axios from 'axios';

// Basert på miljø:
// - Hvis REACT_APP_API_URL er satt (dev eller prod), bruk den eksplisitt
// - Hvis ikke: i prod antar vi at backend ligger på samme origin under /api
// - I dev faller vi tilbake til lokal backend på http://localhost:5001/api
const API_BASE_URL =
  process.env.REACT_APP_API_URL ||
  (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:5001/api');

const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Org-kontekst (superadmin): klienten sender valgt organisasjon som kontroll-header.
// Serveren eier konteksten (signert cookie); headeren hindrer at en fane med gammel
// kontekst skriver i en annen organisasjon. Settes fra AuthContext.
let orgKontekstId: number | null = null;

export const settOrgKontekstHeader = (id: number | null): void => {
  orgKontekstId = id;
};

// CSRF Protection (SEC-009)
// Fetch and cache CSRF token
let csrfToken: string | null = null;

const fetchCsrfToken = async (): Promise<string> => {
  try {
    const response = await axios.get(`${API_BASE_URL}/csrf-token`, {
      withCredentials: true,
    });
    const token = response.data.csrfToken;
    if (!token) {
      throw new Error('CSRF token not found in response');
    }
    csrfToken = token;
    return token;
  } catch (error) {
    console.error('Failed to fetch CSRF token:', error);
    throw error;
  }
};

// Request interceptor: Add CSRF token to state-changing requests
api.interceptors.request.use(
  async (config) => {
    const method = config.method?.toUpperCase();
    
    // Skip CSRF token for authentication endpoints (they are exempt from CSRF protection)
    const authEndpoints = ['/auth/login', '/auth/logout', '/auth/forgot-password', '/auth/reset-password'];
    const isAuthEndpoint = authEndpoints.some(endpoint => config.url?.includes(endpoint));
    
    // Add CSRF token for POST, PUT, DELETE, PATCH (except auth endpoints)
    if (method && ['POST', 'PUT', 'DELETE', 'PATCH'].includes(method) && !isAuthEndpoint) {
      // Fetch token if we don't have one
      if (!csrfToken) {
        await fetchCsrfToken();
      }
      
      if (csrfToken) {
        config.headers['X-CSRF-Token'] = csrfToken;
      }
    }
    
    if (orgKontekstId !== null) {
      config.headers['X-Org-Kontekst'] = String(orgKontekstId);
    }

    // If sending FormData, remove default Content-Type header to let axios set it automatically
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor: Handle CSRF errors and session expiry
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Org-kontekst er endret (409) eller mangler (428): siden må lastes på nytt.
    // Konteksten tas aldri stille i bruk i en åpen side, så vi bare varsler AuthContext.
    const kode = error?.response?.data?.kode;
    if (
      (error?.response?.status === 409 && kode === 'ORG_KONTEKST_ENDRET') ||
      (error?.response?.status === 428 && kode === 'ORG_KONTEKST_MANGLER')
    ) {
      window.dispatchEvent(new CustomEvent('org-kontekst-ugyldig'));
    }

    // Handle CSRF token errors
    if (error.response?.status === 403 && error.response?.data?.error?.includes('CSRF')) {
      console.warn('CSRF token invalid, fetching new token...');
      // Clear old token and retry once
      csrfToken = null;
      
      try {
        await fetchCsrfToken();
        // Retry original request with new token
        error.config.headers['X-CSRF-Token'] = csrfToken;
        return api.request(error.config);
      } catch (retryError) {
        console.error('Failed to retry with new CSRF token:', retryError);
        return Promise.reject(retryError);
      }
    }
    
    // Handle session expiry (kun på beskyttede sider)
    if (error.response?.status === 401) {
      csrfToken = null; // Clear CSRF token on logout
      
      // Ikke vis session-expired dialog på offentlige sider
      const publicRoutes = ['/login', '/forgot-password', '/reset-password', '/auth/callback'];
      const currentPath = window.location.pathname;
      const isPublicRoute = publicRoutes.some(route => currentPath.startsWith(route));
      
      if (!isPublicRoute) {
        const evt = new Event('session-expired');
        window.dispatchEvent(evt);
      }
    }
    
    return Promise.reject(error);
  }
);

export default api;
export { API_BASE_URL };

